/**
 * Dual-layer referral code persistence.
 * Layer 1: localStorage (fast access, can be cleared by OS on iOS).
 * Layer 2: @capacitor/preferences (persistent across app restarts on native).
 *
 * All code that reads/writes fpm_referral_code MUST use these functions.
 */

import { Preferences } from '@capacitor/preferences';
import { isNative } from '@/lib/platform';

const KEY = 'fpm_referral_code';

/** Persist referral code to both localStorage and Capacitor Preferences. */
export async function saveReferralCode(code: string) {
  const upper = code.toUpperCase();
  localStorage.setItem(KEY, upper);
  if (isNative) {
    await Preferences.set({ key: KEY, value: upper });
  }
}

/**
 * Read referral code: tries localStorage first, then falls back to
 * Capacitor Preferences on native (and restores localStorage if found).
 */
export async function getReferralCode(): Promise<string | null> {
  const local = localStorage.getItem(KEY);
  if (local && /^FPM-[A-Z0-9]{8}$/.test(local)) return local;

  // localStorage was empty or invalid — try Preferences on native
  if (isNative) {
    try {
      const { value } = await Preferences.get({ key: KEY });
      if (value && /^FPM-[A-Z0-9]{8}$/.test(value)) {
        localStorage.setItem(KEY, value); // restore fast-access layer
        return value;
      }
    } catch (e) {
      console.warn('Preferences.get failed:', e);
    }
  }

  // Clear invalid values
  if (local) localStorage.removeItem(KEY);
  return null;
}

/** Clear referral code from both layers. */
export async function clearReferralCode() {
  localStorage.removeItem(KEY);
  if (isNative) {
    try {
      await Preferences.remove({ key: KEY });
    } catch (e) {
      console.warn('Preferences.remove failed:', e);
    }
  }
}

/**
 * Single canonical capture function — extracts ?ref= from any URL string
 * and persists it. Works identically for web URLs and native deep links.
 */
export async function captureReferralFromUrl(url: string): Promise<string | null> {
  try {
    const search = url.includes('?') ? url.split('?').slice(1).join('?') : '';
    const params = new URLSearchParams(search);
    const ref = params.get('ref');
    if (ref && /^FPM-[A-Z0-9]{8}$/i.test(ref)) {
      await saveReferralCode(ref);
      console.log('Referral code captured:', ref.toUpperCase());
      return ref.toUpperCase();
    }
  } catch (e) {
    console.warn('captureReferralFromUrl failed:', e);
  }
  return null;
}

/**
 * Recover referral code from Preferences if localStorage was cleared
 * (e.g. iOS memory pressure). Call on every app foreground.
 */
export async function recoverReferralCode(): Promise<void> {
  if (!isNative) return;
  const existing = localStorage.getItem(KEY);
  if (existing && /^FPM-[A-Z0-9]{8}$/.test(existing)) return; // already present
  try {
    const { value } = await Preferences.get({ key: KEY });
    if (value && /^FPM-[A-Z0-9]{8}$/.test(value)) {
      localStorage.setItem(KEY, value);
    }
  } catch (e) {
    console.warn('recoverReferralCode failed:', e);
  }
}
